package org.moxultimate.topwithmox.showscrud.update

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.shows.data.ShowsResponse
import org.moxultimate.topwithmox.showscrud.main.ShowsCRUDState
import org.moxultimate.topwithmox.showscrud.main.ShowsCRUDViewModel
import org.moxultimate.topwithmox.showscrud.main.ShowsFormEntry
import org.moxultimate.topwithmox.showscrud.main.toFormEntry
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.buttons.PressableButtonStyle
import org.moxultimate.topwithmox.components.crud.CRUDDropdown
import org.moxultimate.topwithmox.components.crud.CRUDExistingDropZone
import org.moxultimate.topwithmox.components.crud.CRUDFormField
import org.moxultimate.topwithmox.components.crud.CRUDStatusBanner
import org.moxultimate.topwithmox.components.crud.CRUDSuccessDialog
import org.moxultimate.topwithmox.components.crud.crudShowTypes
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.utils.autoTagSync
import org.moxultimate.topwithmox.theme.FontFamily

/**
 * ShowsUpdatePage
 * Edit page for a single existing show. Pre-populates all fields from [show]
 * and calls PUT /shows/{id} on save or DELETE /shows/{id} on delete.
 *
 * @param show The show to edit, reconstructed from nav params by the route initialiser.
 */
@Composable
fun ShowsUpdatePage(show: ShowsResponse) {
    val viewModel: ShowsCRUDViewModel = koinViewModel()
    val crudState by viewModel.crudState.collectAsState()
    val navigator = rememberAppNavigator()

    var entry by remember { mutableStateOf(show.toFormEntry()) }

    if (crudState is ShowsCRUDState.Success) {
        CRUDSuccessDialog(
            message = (crudState as ShowsCRUDState.Success).message,
            onOkay = {
                viewModel.resetState()
                navigator.goToShowsCRUD()
            },
        )
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .backgroundColor(Colors.White)
            .overflow(Overflow.Auto),
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(
            modifier = Modifier
                .width(clamp(min = 900.px, value = 80.vw, max = 1400.px))
                .padding(top = 80.px, bottom = 32.px, leftRight = 2.cssRem),
            verticalArrangement = Arrangement.spacedBy(24.px),
        ) {
            // Header: title + back button
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(4.px)) {
                    SpanText(
                        text = "Edit Show",
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(32.px)
                            .fontWeight(FontWeight.Bold)
                            .moxTextGradient(),
                    )
                    SpanText(
                        text = "ID: ${show.id}  ·  ${show.type.orEmpty()}",
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(13.px)
                            .color(Colors.Gray),
                    )
                }
                SpanText(
                    text = "← Back",
                    modifier = PressableButtonStyle.toModifier()
                        .fontFamily(FontFamily)
                        .fontSize(14.px)
                        .color(Colors.Gray)
                        .onClick { navigator.goBack() },
                )
            }

            // Status banner for errors
            when (crudState) {
                is ShowsCRUDState.Error -> CRUDStatusBanner(
                    message = (crudState as ShowsCRUDState.Error).message,
                    isError = true,
                    onDismiss = { viewModel.resetState() },
                )
                else -> {}
            }

            // Thumbnail image — existing preview + optional replacement
            CRUDExistingDropZone(
                label = "Thumbnail Image",
                existingUrl = show.thumbnailPath.orEmpty(),
                file = entry.imageFile,
                minHeight = 200,
                onFileDropped = { entry = entry.copy(imageFile = it) },
                onFileRemoved = { entry = entry.copy(imageFile = null) },
            )

            // Title + Type
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.px),
            ) {
                Box(modifier = Modifier.weight(1f)) {
                    CRUDFormField(
                        label = "Title *",
                        value = entry.title,
                        placeholder = "Show title",
                        onValueChange = { entry = entry.copy(title = it, tags = autoTagSync(entry.tags, entry.title, it)) },
                    )
                }
                Box(modifier = Modifier.weight(1f)) {
                    CRUDDropdown(
                        label = "Type *",
                        options = crudShowTypes,
                        selected = entry.type,
                        placeholder = "Select type…",
                        onSelected = { entry = entry.copy(type = it, tags = autoTagSync(entry.tags, entry.type, it)) },
                    )
                }
            }

            // Tags
            CRUDFormField(
                label = "Tags",
                value = entry.tags,
                placeholder = "Pipe-separated tags e.g. Action | Drama (optional)",
                onValueChange = { entry = entry.copy(tags = it) },
            )

            // Description + Video Link
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.px),
            ) {
                Box(modifier = Modifier.weight(1f)) {
                    CRUDFormField(
                        label = "Description",
                        value = entry.description,
                        placeholder = "Short description of the show",
                        onValueChange = { entry = entry.copy(description = it) },
                    )
                }
                Box(modifier = Modifier.weight(1f)) {
                    CRUDFormField(
                        label = "Video Link",
                        value = entry.videoLink,
                        placeholder = "YouTube or external video URL (optional)",
                        onValueChange = { entry = entry.copy(videoLink = it) },
                    )
                }
            }

            // Save + Delete row
            val isLoading = crudState is ShowsCRUDState.Loading
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                GradientButton(
                    text = if (isLoading) "Saving…" else "Save Changes",
                    disabled = !entry.isValidForUpdate || isLoading,
                    onClick = {
                        if (entry.isValidForUpdate && !isLoading) viewModel.updateShow(show.id, entry)
                    },
                )
                GradientButton(
                    text = if (isLoading) "Please wait…" else "🗑 Delete",
                    disabled = isLoading,
                    onClick = { if (!isLoading) viewModel.deleteShow(show.id) },
                )
            }
        }
    }
}
